import React, { useEffect, useRef } from 'react';

import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { useTracking } from '@change-corgi/core-react/tracking';

import { parseSourceInfoFromUrl } from '#src/app/shared/utils/petitionVideo';

import { SupporterCommentCard } from '../SupporterVoicesComments/components/SupporterCommentCard';
import { CreateVideo } from '../SupporterVoicesVideos/components/CreateVideo';
import { LazyVideoComponent } from '../SupporterVoicesVideos/components/LazyVideoComponent';
import { ViewPetitionFeed } from '../SupporterVoicesVideos/components/ViewPetitionFeed';

import { useSupporterVoicesFeedContext } from './shared/context';

export const SupporterVoicesFeed: React.FC<EmptyProps> = () => {
	const {
		data: { feedItems, petition, currentlyPlaying, showCreateVideo, itemsPerRow },
		actions: { setCurrentlyPlaying, removeVideoFromState },
	} = useSupporterVoicesFeedContext();

	const track = useTracking();
	const trackedPetitionIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (trackedPetitionIdRef.current === petition.id) return;
		if (feedItems.length === 0) return;
		trackedPetitionIdRef.current = petition.id;
		void track('petition_page_supporters_voice_view', {
			petition_id: petition.id,
			show_supporter_voices: true,
		});
	}, [petition.id, feedItems.length, track]);

	const renderItem = (item: (typeof feedItems)[number]) => {
		if (item.type === 'comment') {
			return (
				// [&>*:first-child]:h-full makes the CommentCard fill the fixed 320px row from the
				// wrapper (the card itself takes no className), matching the DS "Fixed Height
				// Container" pattern.
				//
				// On desktop these are the row's flexible items, absorbing whatever width the
				// aspect-ratio-sized videos leave over (CHANGE-97645). lg:flex-1 overrides both
				// w-[400px] (basis 0) and shrink-0; lg:min-w-0 releases the automatic minimum size
				// so the card can shrink below its text's min-content width. The fixed width stays
				// unprefixed because the sub-lg carousel needs it.
				<div key={`comment-${item.data.id}`} className="h-[320px] w-[400px] shrink-0 lg:min-w-0 lg:flex-1">
					<SupporterCommentCard
						className="h-full w-full [&>*:first-child]:h-full"
						supporterComment={item.data}
						petition={petition}
					/>
				</div>
			);
		}

		const sourceInfo = parseSourceInfoFromUrl(item.data.videoUrl);
		const poster = 'poster' in sourceInfo ? sourceInfo.poster : undefined;
		return (
			// flex so the video stretches to the 320px height; its width then follows from the
			// video's aspect ratio.
			<div key={`video-${item.data.id}`} className="flex h-[320px] min-h-0 shrink-0">
				<LazyVideoComponent
					poster={poster}
					className="h-full"
					video={item.data}
					currentlyPlaying={currentlyPlaying}
					setCurrentlyPlaying={setCurrentlyPlaying}
					removeVideoFromState={removeVideoFromState}
				/>
			</div>
		);
	};

	const lastRowCount = feedItems.length % itemsPerRow || itemsPerRow;
	const leadingItems = feedItems.slice(0, feedItems.length - lastRowCount);
	const lastRowItems = feedItems.slice(feedItems.length - lastRowCount);

	return (
		<div data-testid="supporter-voices-feed">
			{/*
			 * lg:overflow-visible rather than lg:overflow-x-visible: `overflow-x: visible` computes
			 * to `auto` while the other axis is `hidden`, so the x-only variant was a no-op and the
			 * feed stayed a scroll container on desktop (CHANGE-97645).
			 */}
			<div className="flex flex-nowrap gap-6 overflow-x-auto overflow-y-hidden pt-6 lg:flex-wrap lg:overflow-visible">
				{leadingItems.map((item) => renderItem(item))}
				<div className="flex shrink-0 flex-nowrap gap-6 lg:w-full lg:flex-wrap">
					{lastRowItems.map((item) => renderItem(item))}

					{showCreateVideo ? (
						// Same flex treatment as a comment card, so the CTA is one equal cell instead of
						// the row's slack sink — lg:grow alone made it absorb every leftover pixel and
						// overhang the grid (CHANGE-97645). lg:[&>*]:max-w-none drops
						// EmptyStateCtaPanel's max-w-[628px] so width is owned here; the panel keeps
						// that cap for the legacy SupporterVoicesVideos carousel, which relies on it.
						<div className="flex h-[320px] shrink-0 lg:min-w-0 lg:flex-1 [&>*]:h-full lg:[&>*]:w-full lg:[&>*]:max-w-none">
							<CreateVideo />
						</div>
					) : null}
				</div>
			</div>

			<ClientRender>
				<div className="pt-13">
					<ViewPetitionFeed petitionId={petition.id} slug={petition.slug} />
				</div>
			</ClientRender>
		</div>
	);
};
